import { postJson } from "@/domain/common/client/ApiClient"
import { toYearDay, toYearDayTime } from "@/domain/common/utils/DateUtils"
import {
  entryPlayerStatusLabel,
  entryStatusLabel,
} from "@/domain/entry/model/Entry"
import {
  divisionLabel,
  genderLabel,
  isPlayerRecordCollected,
  sportLabel,
  weightClassLabel,
} from "@/domain/fight/model/Fight"
import { EmptyResponsePayload } from "@bracket/idl/main/common/payload/EmptyRequestPayloadPt"
import {
  EntryCancelRequestPayload,
  EntryGetByEntryNoRequestPayload,
  EntryGetByEntryNoResponsePayload,
  EntryGetByGymRequestPayload,
  EntryGetByGymResponsePayload,
  EntryModifyRequestPayload,
  EntryRegisterRequestPayload,
  EntryRegisterResponsePayload,
} from "@bracket/idl/main/entry/payload/EntryPayloadPt"
import { fightSportPayloadFromJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import { genderPayloadFromJSON } from "@bracket/idl/main/fight/model/enums/GenderPt"
import { WeightClassPayload } from "@bracket/idl/main/fight/model/FightPt"
import type {
  EntryPayload,
  EntryPlayerPayload,
  EntrySummaryPayload,
} from "@bracket/idl/main/entry/model/EntryPt"
import type {
  EntryDetail,
  EntryGymInput,
  EntryPlayerInput,
  EntryPlayerSummary,
  EntrySummary,
} from "@/domain/entry/model/Entry"

interface EntryRegisterParam {
  tournamentId: string
  gym: EntryGymInput
  players: EntryPlayerInput[]
  isFalseStatementAgreed: boolean
  password: string
}

/** 상세 조회 · 수정 · 취소의 본인 확인 값 */
interface EntryVerifyParam {
  entryNo: string
  gymContactPhoneNo: string
  password: string
}

interface EntryGymVerifyParam {
  gymContactPhoneNo: string
  password: string
}

interface EntryModifyParam extends EntryVerifyParam {
  gym: EntryGymInput
  players: EntryPlayerInput[]
}

/**
 * 폼 입력값(문자열)을 IDL 선수 payload 로 옮긴다
 *
 * 부(division)는 서버가 생년월일로 정하므로 싣지 않는다.
 * 체급은 신청자가 고른 값을 그대로 싣는다 — 고를 체급이 없는 부는 비워 보내고 운영자가 확정한다.
 * 닭싸움은 이름 · 생년월일 · 성별 · 종목 넷만 보낸다(체급은 서버가 무체급을 넣는다).
 */
function toPlayerPayload(player: EntryPlayerInput) {
  const sport = fightSportPayloadFromJSON(player.sport)
  // 닭싸움은 기록을 받지 않는다 — 종목을 바꾸기 전에 적어 둔 값이 남아 있어도 싣지 않는다
  const isRecordCollected = isPlayerRecordCollected(sport)

  return {
    id: "",
    name: player.name.trim(),
    birthDate: player.birthDate,
    gender: genderPayloadFromJSON(player.gender),
    sport,
    weightKg: isRecordCollected ? player.weightKg : "",
    careerYears: isRecordCollected ? player.careerYears : "",
    competitionCount: isRecordCollected ? Number(player.competitionCount) : 0,
    // 비어 있어도 기본값이 채워진 payload 를 보낸다 (undefined 필드가 JSON 에 새어 나가지 않게)
    weightClass:
      player.weightClass?.payload ?? WeightClassPayload.fromPartial({ sport }),
  }
}

function toGymPayload(gym: EntryGymInput) {
  return {
    name: gym.name.trim(),
    contactName: gym.contactName.trim(),
    contactPhoneNo: gym.contactPhoneNo.trim(),
  }
}

function toPlayerSummary(player: EntryPlayerPayload): EntryPlayerSummary {
  // 기록을 받지 않는 종목(닭싸움)은 표시할 값이 없다 — "kg" · "경력 년" 만 남지 않게 빈 문자열로 둔다
  const isRecordCollected = isPlayerRecordCollected(player.sport)
  const careerText = `경력 ${player.careerYears}년 · ${player.competitionCount}회`

  return {
    id: player.id,
    name: player.name,
    birthDate: player.birthDate,
    genderLabel: genderLabel(player.gender),
    sportLabel: sportLabel(player.sport),
    sport: player.sport,
    weightKg: player.weightKg,
    careerYears: player.careerYears,
    competitionCount: String(player.competitionCount),
    weightText: isRecordCollected ? `${player.weightKg}kg` : "",
    careerText: isRecordCollected ? careerText : "",
    divisionLabel: divisionLabel(player.division),
    weightClassName: weightClassLabel(player.weightClass),
    status: player.status,
    statusLabel: entryPlayerStatusLabel(player.status),
    rejectedReason: player.rejectedReason,
  }
}

function toDetail(
  entry: EntryPayload,
  tournamentName: string,
  tournamentStartAt: string,
): EntryDetail {
  return {
    id: entry.id,
    entryNo: entry.entryNo,
    tournamentId: entry.tournamentId,
    gymName: entry.gym?.name ?? "",
    gymContactName: entry.gym?.contactName ?? "",
    gymContactPhoneNo: entry.gym?.contactPhoneNo ?? "",
    statusLabel: entryStatusLabel(entry.status),
    status: entry.status,
    canceledReason: entry.canceledReason,
    players: entry.player.map(toPlayerSummary),
    tournamentName,
    tournamentPeriodText: toYearDay(tournamentStartAt),
  }
}

function toSummary(summary: EntrySummaryPayload): EntrySummary {
  return {
    id: summary.id,
    entryNo: summary.entryNo,
    tournamentId: summary.tournamentId,
    tournamentName: summary.tournamentName,
    tournamentStartText: toYearDay(summary.tournamentStartAt),
    gymName: summary.gymName,
    gymContactName: summary.gymContactName,
    status: summary.status,
    statusLabel: entryStatusLabel(summary.status),
    playerCount: summary.playerCount,
    approvedPlayerCount: summary.approvedPlayerCount,
    rejectedPlayerCount: summary.rejectedPlayerCount,
    sportLabels: summary.sport.map(sportLabel).join(", "),
    appliedAtText: toYearDayTime(summary.appliedAt),
    canceledReason: summary.canceledReason,
  }
}

/**
 * 참가 신청 도메인 클라이언트.
 *
 * 경로는 fight-bracket-server 의 `EntryRouter` (google.api.http) 와 1:1 대응한다.
 * 인증이 없어 조회 · 수정 · 취소는 **체육관 연락처 + 신청 비밀번호**가 함께 맞아야 통과한다.
 */
const EntryClient = {
  /** 신청 등록 — 발급된 신청번호를 돌려준다 */
  async register(param: EntryRegisterParam): Promise<string> {
    const request = EntryRegisterRequestPayload.create({
      tournamentId: param.tournamentId,
      gym: toGymPayload(param.gym),
      player: param.players.map(toPlayerPayload),
      isFalseStatementAgreed: param.isFalseStatementAgreed,
      password: param.password,
    })

    const json = await postJson<unknown>(
      "/entry/register",
      EntryRegisterRequestPayload.toJSON(request),
    )
    const response = EntryRegisterResponsePayload.fromJSON(json)

    return response.entryNo
  },

  /** 체육관 신청 목록 — 신청번호를 몰라도 연락처 + 비밀번호로 찾는다 */
  async getByGym(param: EntryGymVerifyParam): Promise<EntrySummary[]> {
    const request = EntryGetByGymRequestPayload.create({
      gymContactPhoneNo: param.gymContactPhoneNo.trim(),
      password: param.password,
    })

    const json = await postJson<unknown>(
      "/entry/get-by-gym",
      EntryGetByGymRequestPayload.toJSON(request),
    )
    const response = EntryGetByGymResponsePayload.fromJSON(json)

    return response.data.map(toSummary)
  },

  async getByEntryNo(param: EntryVerifyParam): Promise<EntryDetail> {
    const request = EntryGetByEntryNoRequestPayload.create({
      entryNo: param.entryNo.trim(),
      gymContactPhoneNo: param.gymContactPhoneNo.trim(),
      password: param.password,
    })

    const json = await postJson<unknown>(
      "/entry/get-by-entry-no",
      EntryGetByEntryNoRequestPayload.toJSON(request),
    )
    const response = EntryGetByEntryNoResponsePayload.fromJSON(json)

    if (!response.data) throw new Error("신청 내역을 찾을 수 없습니다.")

    return toDetail(
      response.data,
      response.tournament?.name ?? "",
      response.tournament?.startAt ?? "",
    )
  },

  /** 신청 수정 — 선수 목록은 부분 수정이 아니라 전체 교체다 */
  async modify(param: EntryModifyParam): Promise<void> {
    const request = EntryModifyRequestPayload.create({
      entryNo: param.entryNo.trim(),
      gymContactPhoneNo: param.gymContactPhoneNo.trim(),
      password: param.password,
      gym: toGymPayload(param.gym),
      player: param.players.map(toPlayerPayload),
    })

    const json = await postJson<unknown>(
      "/entry/modify",
      EntryModifyRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },

  async cancel(param: EntryVerifyParam & { reason: string }): Promise<void> {
    const request = EntryCancelRequestPayload.create({
      entryNo: param.entryNo.trim(),
      gymContactPhoneNo: param.gymContactPhoneNo.trim(),
      password: param.password,
      reason: param.reason.trim(),
    })

    const json = await postJson<unknown>(
      "/entry/cancel",
      EntryCancelRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },
}

export { EntryClient, toDetail, toPlayerSummary, toSummary }
export type {
  EntryGymVerifyParam,
  EntryModifyParam,
  EntryRegisterParam,
  EntryVerifyParam,
}
